<template>
  <div id="app" class="w-screen min-h-screen">
    <router-view v-slot="{ Component, route }">
      <Transition name="app-page" mode="out-in" appear>
        <div :key="getTopLevelRouteKey(route)">
          <component :is="Component" />
        </div>
      </Transition>
    </router-view>

    <Transition name="version-alert">
      <div v-if="versionAlert.show" class="fixed inset-x-0 top-5 z-50 mx-auto w-full max-w-lg px-4 sm:px-0">
        <div class="overflow-hidden rounded-xl border border-yellow-200 bg-white shadow-2xl ring-1 ring-black ring-opacity-5">
          <div class="flex items-start gap-4 p-5">
            <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100">
              <InformationCircleIcon class="h-5 w-5 text-yellow-500" aria-hidden="true" />
            </div>

            <div class="min-w-0 flex-1">
              <p class="text-sm font-semibold text-gray-900">Dostępna jest nowa wersja systemu.</p>
              <p class="mt-1 text-sm leading-5 text-gray-600">Odśwież stronę, żeby pobrać najnowszy build aplikacji.</p>

              <div class="mt-4 flex flex-wrap items-center gap-3">
                <button @click="refreshApp()" class="inline-flex items-center gap-2 rounded-md bg-yellow-500 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition duration-200 ease-out hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 active:scale-[0.96]">
                  Odśwież
                  <RefreshIcon class="h-4 w-4" />
                </button>
                <button @click="closeAlert()" class="inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-gray-600 transition duration-200 ease-out hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 active:scale-[0.96]">
                  Później
                </button>
              </div>
            </div>

            <button @click="closeAlert()" class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-gray-400 transition duration-200 ease-out hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2">
              <span class="sr-only">Zamknij</span>
              <XMarkIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <Notification />
  </div>
</template>

<script>
import { XIcon as XMarkIcon } from '@heroicons/vue/solid';
import { RefreshIcon, InformationCircleIcon } from '@heroicons/vue/solid';
import Notification from '@/components/Notification.vue';
import appVersion from '@/app-version.json';

const VERSION_ACKNOWLEDGED_BUILD_ID_KEY = 'jan-art:version-acknowledged-build-id';
// Jak często pytamy serwer o wersję. Wcześniej co minutę – przy zmianach kodu co kilka minut
// generowało to więcej szumu niż pożytku.
const VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;

export default {
  components: {
    XMarkIcon,
    RefreshIcon,
    InformationCircleIcon,
    Notification,
  },
  data() {
    return {
      versionAlert: {
        show: false,
        currentBuildId: this.getCurrentBuildId(),
        latestBuildId: "",
        dismissedBuildId: this.getAcknowledgedBuildId(),
      },
      versionCheckInterval: null,
      versionCheckInProgress: false,
    }
  },
  computed: {
    user() {
      return this.$store.state.user || null;
    },
    authIsReady() {
      return !!this.$store.state.authIsReady;
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        if(!this.authIsReady || user || this.isPublicRoute(this.$route)) return;
        this.$router.replace("/");
      },
    },
    authIsReady: {
      immediate: true,
      handler(ready) {
        if(!ready || this.user || this.isPublicRoute(this.$route)) return;
        this.$router.replace("/");
      },
    },
    $route: {
      immediate: true,
      handler(to) {
        this.setMeta(to?.meta || {});
      },
    },
  },
  methods: {
    closeAlert() {
      this.versionAlert.show = false;
      this.acknowledgeBuildId(this.versionAlert.latestBuildId);
    },
    // Jedno źródło prawdy: ostatni build, który użytkownik przyjął do wiadomości.
    // Wcześniej ten sam stan był trzymany w dwóch kluczach localStorage i czterech polach,
    // co niepotrzebnie komplikowało logikę i dawało sprzeczne odpowiedzi.
    getAcknowledgedBuildId() {
      try {
        return window.localStorage.getItem(VERSION_ACKNOWLEDGED_BUILD_ID_KEY) || "";
      } catch (error) {
        return "";
      }
    },
    isBuildIdHandled(buildId) {
      const normalizedBuildId = String(buildId || '').trim();
      return !!normalizedBuildId && normalizedBuildId === this.versionAlert.dismissedBuildId;
    },
    acknowledgeBuildId(buildId) {
      const normalizedBuildId = String(buildId || '').trim();
      if(!normalizedBuildId) return;

      this.versionAlert.dismissedBuildId = normalizedBuildId;
      if(this.versionAlert.latestBuildId === normalizedBuildId)
      {
        this.versionAlert.show = false;
      }

      try {
        window.localStorage.setItem(VERSION_ACKNOWLEDGED_BUILD_ID_KEY, normalizedBuildId);
      } catch (error) {
        console.log(error?.message || error);
      }
    },
    handleVersionStorageChange(event) {
      if(event.key !== VERSION_ACKNOWLEDGED_BUILD_ID_KEY) return;

      this.versionAlert.dismissedBuildId = this.getAcknowledgedBuildId();
      if(this.versionAlert.latestBuildId && this.isBuildIdHandled(this.versionAlert.latestBuildId))
      {
        this.versionAlert.show = false;
      }
    },
    setMeta(meta) {
      document.title = meta.pageTitle || 'Jan-Art System';
    },
    getTopLevelRouteKey(route) {
      return route?.matched?.[0]?.path || route?.path || 'app-view';
    },
    isPublicRoute(route) {
      if(!route) return false;
      if(route.path === "/" || route.path === "/pos") return true;
      if(route.name === "Confirm delivery") return true;
      return route.matched?.some((match) => match.meta?.public === true) || false;
    },
    async fetchAppVersion() {
      const response = await fetch(`/app-version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if(!response.ok)
      {
        throw new Error(`Version check failed: ${response.status}`);
      }

      return response.json();
    },
    async checkAppVersion() {
      if(this.versionCheckInProgress) return;

      try {
        this.versionCheckInProgress = true;
        const latest = await this.fetchAppVersion();
        const latestBuildId = String(latest?.buildId || '').trim();
        const latestEnvironment = String(latest?.environment || '').trim();
        const currentEnvironment = process.env.VUE_APP_VERSION || appVersion?.environment || 'UNKNOWN';

        if(!latestBuildId) return;
        if(latestEnvironment && latestEnvironment !== currentEnvironment) return;

        if(latestBuildId === this.versionAlert.currentBuildId)
        {
          this.versionAlert.latestBuildId = latestBuildId;
          this.acknowledgeBuildId(latestBuildId);
          return;
        }

        if(this.isBuildIdHandled(latestBuildId))
        {
          this.versionAlert.latestBuildId = latestBuildId;
          this.versionAlert.show = false;
          return;
        }

        if(!this.versionAlert.currentBuildId)
        {
          this.versionAlert.currentBuildId = latestBuildId;
          return;
        }

        if(latestBuildId !== this.versionAlert.currentBuildId && latestBuildId !== this.versionAlert.dismissedBuildId)
        {
          this.versionAlert.latestBuildId = latestBuildId;
          this.versionAlert.show = true;
        }
      } catch (error) {
        console.log(error?.message || error);
      } finally {
        this.versionCheckInProgress = false;
      }
    },
    startVersionChecker() {
      this.checkAppVersion();
      this.versionCheckInterval = window.setInterval(() => {
        this.checkAppVersion();
      }, VERSION_CHECK_INTERVAL_MS);

      // 'visibilitychange' w zupełności wystarcza po powrocie do karty – nasłuch 'focus'
      // odpalał to samo sprawdzenie przy każdym kliknięciu w okno.
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('storage', this.handleVersionStorageChange);
    },
    stopVersionChecker() {
      if(this.versionCheckInterval)
      {
        window.clearInterval(this.versionCheckInterval);
        this.versionCheckInterval = null;
      }

      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('storage', this.handleVersionStorageChange);
    },
    handleVisibilityChange() {
      if(document.visibilityState === 'visible')
      {
        this.checkAppVersion();
      }
    },
    refreshApp() {
      this.acknowledgeBuildId(this.versionAlert.latestBuildId);

      // Zwykłe przeładowanie wystarczy: index.html jest serwowany z 'no-store', a pliki JS/CSS
      // mają hash w nazwie. Dopisywanie '?appBuild=' było zbędne, a zostawało w adresie na stałe
      // (trafiało do zakładek i kopiowanych linków).
      window.location.reload();
    },
    getCurrentBuildId() {
      const environment = process.env.VUE_APP_VERSION || appVersion?.environment || 'UNKNOWN';
      const gitSha = appVersion?.gitSha || 'unknown';
      const builtAt = appVersion?.builtAt || '';
      if(appVersion?.environment === environment && appVersion?.buildId)
      {
        return String(appVersion.buildId);
      }
      return `${environment}-${gitSha}-${builtAt}`;
    },
    getBuildLabel() {
      const environment = process.env.VUE_APP_VERSION || appVersion?.environment || 'UNKNOWN';
      const gitSha = appVersion?.gitSha || 'unknown';
      return `${environment}-${gitSha}`;
    },
    resolveApiConfig() {
      if(process.env.VUE_APP_VERSION === 'SANDBOX_LOCAL') {
        return {
          apiLink: "http://127.0.0.1:5001/jan-art-vtest/europe-central2/api",
          herokuApiLink: "http://127.0.0.1:3000/api",
          herokuApiPath: "sandbox",
        };
      }

      if(process.env.VUE_APP_VERSION === 'SANDBOX') {
        return {
          apiLink: "https://europe-central2-jan-art-vtest.cloudfunctions.net/api",
          herokuApiLink: "https://janart-app-2d9773139e0a.herokuapp.com/api",
          herokuApiPath: "sandbox",
        };
      }

      if(process.env.VUE_APP_VERSION === 'PRODUCTION_LOCAL') {
        return {
          apiLink: "http://127.0.0.1:5001/jan-art/europe-central2/api",
          herokuApiLink: "http://127.0.0.1:3000/api",
          herokuApiPath: "production",
        };
      }

      return {
        apiLink: "https://europe-central2-jan-art.cloudfunctions.net/api",
        herokuApiLink: "https://janart-app-2d9773139e0a.herokuapp.com/api",
        herokuApiPath: "production",
      };
    },
  },
  created() {
    const apiConfig = this.resolveApiConfig();
    this.$store.state.apiLink = apiConfig.apiLink;
    this.$store.state.herokuApiLink = apiConfig.herokuApiLink;
    this.$store.state.herokuApiPath = apiConfig.herokuApiPath;
    this.$store.commit('setAppState', {
      active: true,
      version: this.getBuildLabel(),
    });
  },
  mounted() {
    this.startVersionChecker();
  },
  beforeUnmount() {
    this.stopVersionChecker();
  },
}
</script>

<style>
* {
  -webkit-tap-highlight-color: transparent;
}

body {
  overflow-x: hidden;
}

.fade-enter-active {
  transition: opacity 1s ease;
}

.fade-leave-active {
  transition: opacity 0s;
  display: none;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade2-enter-active,
.fade2-leave-active {
  transition: opacity 0.5s ease;
}

.fade2-enter-from,
.fade2-leave-to {
  opacity: 0;
}

.version-alert-enter-active {
  transition:
    opacity 260ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 360ms cubic-bezier(0.16, 1, 0.3, 1),
    filter 320ms cubic-bezier(0.16, 1, 0.3, 1);
}

.version-alert-leave-active {
  transition:
    opacity 140ms cubic-bezier(0.7, 0, 0.84, 0),
    transform 180ms cubic-bezier(0.7, 0, 0.84, 0),
    filter 140ms cubic-bezier(0.7, 0, 0.84, 0);
}

.version-alert-enter-from,
.version-alert-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.985);
  filter: blur(4px);
}

.version-alert-enter-to,
.version-alert-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
  filter: blur(0);
}

.app-page-enter-active {
  transition:
    opacity 160ms cubic-bezier(0.2, 0, 0, 1),
    transform 180ms cubic-bezier(0.2, 0, 0, 1);
}

.app-page-leave-active {
  transition:
    opacity 90ms cubic-bezier(0.4, 0, 1, 1),
    transform 90ms cubic-bezier(0.4, 0, 1, 1);
}

.app-page-enter-from {
  opacity: 0;
  transform: scale(0.998);
}

.app-page-leave-to {
  opacity: 0;
  transform: scale(0.999);
}

.app-page-enter-to,
.app-page-leave-from {
  opacity: 1;
  transform: scale(1);
}

@media (prefers-reduced-motion: reduce) {
  .app-page-enter-active,
  .app-page-leave-active,
  .version-alert-enter-active,
  .version-alert-leave-active {
    transition: opacity 1ms linear;
  }

  .app-page-enter-from,
  .app-page-leave-to,
  .version-alert-enter-from,
  .version-alert-leave-to {
    opacity: 0;
    transform: none;
    filter: none;
  }
}
</style>
